import { useContext, useMemo } from "react";
import Collapse from "antd/es/collapse/Collapse";
import Tag from "antd/es/tag";
import currency from "currency.js";
import {
  IStrategyOverview,
  useStrategies,
} from "../../hooks/api/admin/useStrategies";
import { DataContext } from "../../context/DataContext";
import { Spinner } from "../shared/Spinner";
import { SpinnerContainerWrapper } from "../shared/Spinner/style";
import { StrategyAccountsTable } from "./StrategyAccountsTable";
import { shortenString } from "../../utils/strings";
import {
  EmptyState,
  PanelLabel,
  PanelTags,
  PanelTitle,
  StrategiesList,
  StrategiesPageWrapper,
  StrategyStat,
  StrategyStatGrid,
  SummaryBar,
  SummaryStat,
} from "./style";

// USD-ish helper for the collateral totals; falls back to the raw string if
// it isn't numeric.
const formatUsd = (value: string) => {
  const n = Number(value);
  return Number.isFinite(n) ? currency(n).format() : value;
};

function StrategyPanelLabel({
  strategy,
  label,
}: {
  strategy: IStrategyOverview;
  label?: string;
}) {
  const pendingDeposits = strategy.pending_deposits_count;
  const pendingWithdrawals = strategy.pending_withdrawals_count;
  const inProcess =
    strategy.in_process_deposits_count + strategy.in_process_withdrawals_count;

  return (
    <PanelLabel>
      <PanelTitle>
        <span>{label || strategy.name || "Unnamed Strategy"}</span>
        <span>{shortenString(strategy.address, 8, 6)}</span>
      </PanelTitle>
      <PanelTags>
        {strategy.state ? <Tag>{strategy.state}</Tag> : null}
        <Tag>{strategy.account_count} accounts</Tag>
        <Tag color={pendingDeposits > 0 ? "green" : undefined}>
          {pendingDeposits} pending deposits
        </Tag>
        <Tag color={pendingWithdrawals > 0 ? "red" : undefined}>
          {pendingWithdrawals} pending withdrawals
        </Tag>
        {inProcess > 0 ? <Tag color="orange">{inProcess} in-process</Tag> : null}
      </PanelTags>
    </PanelLabel>
  );
}

export function Strategies() {
  const { data, isLoading, error } = useStrategies();
  const { records } = useContext(DataContext);

  const strategies: IStrategyOverview[] = useMemo(() => data || [], [data]);

  // Sort strategies with pending activity to the top so the ops-relevant
  // ones surface first.
  const sorted = useMemo(
    () =>
      [...strategies].sort((a, b) => {
        const aPending =
          a.pending_deposits_count + a.pending_withdrawals_count;
        const bPending =
          b.pending_deposits_count + b.pending_withdrawals_count;
        if (aPending !== bPending) return bPending - aPending;
        return (a.name || "").localeCompare(b.name || "");
      }),
    [strategies]
  );

  const totals = useMemo(
    () =>
      strategies.reduce(
        (acc, s) => ({
          pendingDeposits: acc.pendingDeposits + s.pending_deposits_count,
          pendingWithdrawals:
            acc.pendingWithdrawals + s.pending_withdrawals_count,
          inProcess:
            acc.inProcess +
            s.in_process_deposits_count +
            s.in_process_withdrawals_count,
        }),
        { pendingDeposits: 0, pendingWithdrawals: 0, inProcess: 0 }
      ),
    [strategies]
  );

  if (isLoading && !data) {
    return (
      <SpinnerContainerWrapper height={"calc(100vh - 80px)"}>
        <Spinner />
      </SpinnerContainerWrapper>
    );
  }

  if (error) {
    return (
      <StrategiesPageWrapper>
        <EmptyState>Failed to load strategies: {error.message}</EmptyState>
      </StrategiesPageWrapper>
    );
  }

  return (
    <StrategiesPageWrapper>
      <SummaryBar>
        <SummaryStat>
          <span>Strategies</span>
          <span>{strategies.length}</span>
        </SummaryStat>
        <SummaryStat>
          <span>Pending Deposits</span>
          <span>{totals.pendingDeposits}</span>
        </SummaryStat>
        <SummaryStat>
          <span>Pending Withdrawals</span>
          <span>{totals.pendingWithdrawals}</span>
        </SummaryStat>
        <SummaryStat>
          <span>In-Process</span>
          <span>{totals.inProcess}</span>
        </SummaryStat>
      </SummaryBar>

      <StrategiesList>
        {sorted.length === 0 ? (
          <EmptyState>No strategies found.</EmptyState>
        ) : (
          <Collapse
            expandIconPosition="end"
            items={sorted.map((strategy) => ({
              key: strategy.address,
              label: (
                <StrategyPanelLabel
                  strategy={strategy}
                  label={records[strategy.address]}
                />
              ),
              children: (
                <>
                  <StrategyStatGrid>
                    <StrategyStat>
                      <span>PPS</span>
                      <div>{strategy.pps}</div>
                    </StrategyStat>
                    <StrategyStat>
                      <span>Cap</span>
                      <div>{strategy.cap}</div>
                    </StrategyStat>
                    <StrategyStat>
                      <span>Target Utilization</span>
                      <div>{strategy.target_utilization}</div>
                    </StrategyStat>
                    <StrategyStat>
                      <span>Deposit Collateral</span>
                      <div>{strategy.deposit_collateral || "—"}</div>
                    </StrategyStat>
                    <StrategyStat>
                      <span>Withdrawal Collateral</span>
                      <div>{strategy.withdrawal_collateral || "—"}</div>
                    </StrategyStat>
                    <StrategyStat>
                      <span>Total Deposits</span>
                      <div>{formatUsd(strategy.total_deposits)}</div>
                    </StrategyStat>
                    <StrategyStat>
                      <span>Total Withdrawals</span>
                      <div>{formatUsd(strategy.total_withdrawals)}</div>
                    </StrategyStat>
                    <StrategyStat>
                      <span>Committed Deposits</span>
                      <div>{strategy.commited_deposits_count}</div>
                    </StrategyStat>
                    <StrategyStat>
                      <span>Committed Withdrawals</span>
                      <div>{strategy.commited_withdrawals_count}</div>
                    </StrategyStat>
                  </StrategyStatGrid>
                  <StrategyAccountsTable
                    accounts={strategy.accounts}
                    isLoading={isLoading}
                  />
                </>
              ),
            }))}
          />
        )}
      </StrategiesList>
    </StrategiesPageWrapper>
  );
}
